import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import API_BASE from "./apiBase";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/**
 * ===========================================================================
 * ROUTE ANALYSIS
 * ===========================================================================
 * Three sources on one map, kept visually distinct because they are different
 * kinds of fact and have different reliability:
 *
 *   PLANNED   amber   where the app said the van was going, written back after
 *                     optimisation
 *   ACTUAL    blue    where it went — a GPS fix every two minutes
 *   STOPS     pins    what happened at each door, written by hand at the door
 *
 * The two-minute interval is why there is no dwell time here. A van can stop,
 * deliver and leave entirely between two fixes, so any "time at door" derived
 * from this trail would be a guess wearing the clothes of a measurement. Dwell
 * analysis comes from the overnight aggregate, which is a separate job.
 *
 * WHAT THIS SCREEN IS ACTUALLY FOR: seeing that drop 14 was done before drop
 * 6, and that the afternoon ran late as a result. Planned order against actual
 * order is the finding; the map is how you see why.
 */

const S = {
  wrap:   { padding: 16, fontFamily: "system-ui, sans-serif", color: "#111827" },
  h1:     { fontSize: 18, fontWeight: 700, margin: 0 },
  panel:  { border: "1px solid #d1d5db", borderRadius: 10, background: "#fcfcfd",
            padding: "10px 12px" },
  label:  { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
            textTransform: "uppercase", letterSpacing: 0.5 },
  input:  { padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db",
            borderRadius: 4 },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th:     { textAlign: "left", padding: "5px 7px", borderBottom: "2px solid #e5e7eb",
            fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
            color: "#6b7280", whiteSpace: "nowrap" },
  td:     { padding: "5px 7px", borderBottom: "1px solid #f3f4f6" },
  muted:  { color: "#6b7280", fontSize: 11 },
  error:  { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
            padding: 10, borderRadius: 6, fontSize: 13 },
};

const COLOUR = {
  planned:   "#f59e0b",   // amber — intention
  trail:     "#2563eb",   // blue  — what happened
  depot:     "#111827",
  delivered: "#16a34a",
  failed:    "#dc2626",
  pending:   "#9ca3af",
};

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const pad2 = (n) => String(n).padStart(2, "0");
const hms = (secs) => {
  if (secs === null || secs === undefined) return "—";
  const s = Math.max(0, Math.round(Number(secs)));
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}`;
};
const clock = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "—"
    : `${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
};
const ukDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

async function post(path, token, body) {
  const res = await fetch(`${API_BASE}/api/reports/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `${path} → ${res.status}`);
  }
  return data;
}

/**
 * The polyline the app stores is a JSON string of [[lat, lng], ...].
 * Parsed defensively: an unparseable route should cost you the amber line, not
 * the whole screen.
 */
function parseRoute(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p) =>
      Array.isArray(p) && p.length === 2 &&
      Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
      .map((p) => [Number(p[0]), Number(p[1])]);
  } catch {
    return [];
  }
}

/** Refit whenever the selected round changes, not just on mount. */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 14); return; }
    map.fitBounds(points, { padding: [30, 30] });
  }, [points, map]);
  return null;
}

export default function RouteAnalysis() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  const masked = useMaskNames();

  const members = useMemo(() => {
    try {
      const m = JSON.parse(localStorage.getItem("members") || "[]");
      return (Array.isArray(m) ? m : [])
        .slice()
        .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    } catch { return []; }
  }, []);

  const now = new Date();
  const [branch, setBranch] = useState("ALL");
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPlanned, setShowPlanned] = useState(true);
  const [showTrail, setShowTrail] = useState(true);

  const accountIds = () => (branch === "ALL"
    ? members.map((m) => String(m.id)).filter(Boolean)
    : [String(branch)]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await post("routeanalysis_list", token, {
          accountIds: accountIds(),
          month: month === "ALL" ? null : Number(month),
          year: year === "ALL" ? null : Number(year),
          page: 1, limit: 100,
        });
        if (cancelled) return;
        const list = data.items || [];
        setItems(list);
        setSelected(list.length ? list[0].deliveryRoundId : null);
      } catch (e) {
        if (!cancelled) { setError(e.message); setItems([]); setSelected(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, month, year]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const d = await post("routeanalysis_detail", token, { deliveryRoundId: selected });
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) { setError(e.message); setDetail(null); }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const planned = useMemo(() => parseRoute(detail?.plannedRoute), [detail]);
  const trail = useMemo(
    () => (detail?.trail || []).map((t) => [t.lat, t.lng]), [detail]);
  const depot = detail?.branch?.lat != null && detail?.branch?.lng != null
    ? [detail.branch.lat, detail.branch.lng] : null;

  const stopsWithCoords = (detail?.stops || [])
    .filter((s) => s.lat != null && s.lng != null);

  const bounds = useMemo(() => {
    const pts = [];
    if (showPlanned) pts.push(...planned);
    if (showTrail) pts.push(...trail);
    stopsWithCoords.forEach((s) => pts.push([s.lat, s.lng]));
    if (depot) pts.push(depot);
    return pts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, showPlanned, showTrail]);

  // Stops delivered out of their planned position. This is the finding the
  // screen exists for, so it is computed once and shown as a count rather than
  // left for someone to spot by reading two columns.
  const outOfOrder = useMemo(() => {
    const acts = (detail?.actual || []).filter((a) => a.plannedPosition != null);
    return acts.filter((a) => a.plannedPosition !== a.actualPosition).length;
  }, [detail]);

  if (!token) return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                    flexWrap: "wrap", marginBottom: 10 }}>
        <h1 style={S.h1}>Route analysis</h1>
        <MaskToggleButton />

        <select style={S.input} value={branch} onChange={(e) => setBranch(e.target.value)}>
          <option value="ALL">All branches</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>

        <select style={S.input} value={month}
                onChange={(e) => setMonth(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}>
          <option value="ALL">All months</option>
          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>

        <select style={S.input} value={year}
                onChange={(e) => setYear(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}>
          <option value="ALL">All years</option>
          {[0, 1, 2].map((back) => {
            const y = now.getFullYear() - back;
            return <option key={y} value={y}>{y}</option>;
          })}
        </select>

        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" checked={showPlanned}
                 onChange={(e) => setShowPlanned(e.target.checked)} />
          <span style={{ color: COLOUR.planned, fontWeight: 700 }}>Planned</span>
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" checked={showTrail}
                 onChange={(e) => setShowTrail(e.target.checked)} />
          <span style={{ color: COLOUR.trail, fontWeight: 700 }}>Actual</span>
        </label>
      </div>

      {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {/* ---------------- rounds ---------------- */}
        <div style={{ ...S.panel, flex: "0 0 340px", maxWidth: 340 }}>
          <div style={{ ...S.muted, marginBottom: 6 }}>
            {loading ? "Loading…" : `${items.length} round${items.length === 1 ? "" : "s"}`}
          </div>
          <div style={{ maxHeight: "72vh", overflowY: "auto" }}>
            {items.length === 0 && !loading ? (
              <div style={S.muted}>No rounds for these filters.</div>
            ) : items.map((it) => {
              const on = it.deliveryRoundId === selected;
              // Only flagged once optimisation actually predicted something.
              const over = it.overrunRatio != null && it.overrunRatio > 1.25;
              return (
                <div key={it.deliveryRoundId}
                  onClick={() => setSelected(it.deliveryRoundId)}
                  style={{ padding: "7px 8px", borderRadius: 6, cursor: "pointer",
                           marginBottom: 4, background: on ? "#f0fdfa" : "#fff",
                           border: on ? "2px solid #0f766e" : "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                    <b style={{ fontSize: 12.5 }}>{ukDate(it.deliveryDate)}</b>
                    <span style={S.muted}>{clock(it.firstDeliveredAt)}–{clock(it.lastDeliveredAt)}</span>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {displayName(it.driverName, masked)}
                    <span style={S.muted}> · {displayName(it.branchName, masked)}</span>
                  </div>
                  <div style={S.muted}>
                    {it.stopsDone}/{it.plannedStops || "?"} stops
                    {it.failCount > 0 && (
                      <span style={{ color: COLOUR.failed, fontWeight: 600 }}>
                        {" "}· {it.failCount} failed
                      </span>
                    )}
                    {over && (
                      <span style={{ color: "#b45309", fontWeight: 600 }}>
                        {" "}· {Math.round((it.overrunRatio - 1) * 100)}% over
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------- map + comparison ---------------- */}
        <div style={{ flex: 1, minWidth: 0, display: "flex",
                      flexDirection: "column", gap: 12 }}>
          <div style={{ ...S.panel, padding: 0, overflow: "hidden", height: "58vh" }}>
            {!detail && !detailLoading ? (
              <div style={{ padding: 12, ...S.muted }}>Pick a round.</div>
            ) : (
              <MapContainer center={depot || [54.5, -3]} zoom={depot ? 12 : 6}
                            style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={bounds} />

                {showPlanned && planned.length > 1 && (
                  <Polyline positions={planned}
                            pathOptions={{ color: COLOUR.planned, weight: 4, opacity: 0.75 }} />
                )}
                {showTrail && trail.length > 1 && (
                  <Polyline positions={trail}
                            pathOptions={{ color: COLOUR.trail, weight: 3, opacity: 0.85 }} />
                )}

                {depot && (
                  <CircleMarker center={depot} radius={8}
                    pathOptions={{ color: "#fff", weight: 2, fillColor: COLOUR.depot, fillOpacity: 1 }}>
                    <Tooltip>{displayName(detail.branch.name, masked)} (depot)</Tooltip>
                  </CircleMarker>
                )}

                {stopsWithCoords.map((s) => {
                  const colour = s.delivered > 0 ? COLOUR.delivered
                    : s.failed > 0 ? COLOUR.failed
                      : COLOUR.pending;
                  return (
                    <CircleMarker key={s.recipientId} center={[s.lat, s.lng]} radius={7}
                      pathOptions={{ color: "#fff", weight: 2, fillColor: colour, fillOpacity: 1 }}>
                      <Tooltip>
                        <b>{s.plannedPosition ?? "—"}. {displayName(s.fullName, masked)}</b>
                        <div>{[s.address, s.postcode].filter(Boolean).join(", ")}</div>
                        <div>{s.parcels} parcel{s.parcels === 1 ? "" : "s"}</div>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            )}
          </div>

          {detail && (
            <div style={S.panel}>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap",
                            marginBottom: 8, fontSize: 12.5 }}>
                <span><b>Planned</b> {hms(detail.round.plannedSeconds)}</span>
                <span><b>Actual</b> {clock(detail.actual[0]?.deliveredAt)}–
                  {clock(detail.actual[detail.actual.length - 1]?.deliveredAt)}</span>
                <span><b>Stops</b> {detail.stops.length}</span>
                <span style={outOfOrder > 0 ? { color: "#b45309", fontWeight: 600 } : {}}>
                  <b>Out of order</b> {outOfOrder}
                </span>
                {/* Said plainly rather than left as an empty map. An absent
                    trail means the app was not posting, which is a different
                    problem from a driver who did not move. */}
                {trail.length === 0 && (
                  <span style={S.muted}>No GPS trail recorded for this round</span>
                )}
                {planned.length === 0 && (
                  <span style={S.muted}>No optimised route stored</span>
                )}
              </div>

              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Planned</th>
                    <th style={S.th}>Actual</th>
                    <th style={S.th}>Customer</th>
                    <th style={S.th}>At</th>
                    <th style={S.th}>Outcome</th>
                    <th style={S.th}>Parcels</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.actual.map((a) => {
                    const stop = detail.stops.find((s) => s.recipientId === a.recipientId);
                    const moved = a.plannedPosition != null
                      && a.plannedPosition !== a.actualPosition;
                    return (
                      <tr key={a.logId} style={moved ? { background: "#fffbeb" } : {}}>
                        <td style={S.td}>{a.plannedPosition ?? "—"}</td>
                        <td style={{ ...S.td, fontWeight: moved ? 700 : 400 }}>
                          {a.actualPosition}
                        </td>
                        <td style={S.td}>
                          {displayName(stop?.fullName || "Unknown", masked)}
                          {stop?.postcode && <span style={S.muted}> {stop.postcode}</span>}
                        </td>
                        <td style={S.td}>{clock(a.deliveredAt)}</td>
                        <td style={{ ...S.td,
                          color: a.success ? COLOUR.delivered : COLOUR.failed,
                          fontWeight: 600 }}>
                          {a.success ? "Delivered" : (a.failureReason || "Failed")}
                        </td>
                        <td style={S.td}>{a.parcels}</td>
                      </tr>
                    );
                  })}
                  {/* Planned stops with no log at all — never attempted. They
                      would otherwise vanish, and a stop nobody went to is the
                      one most worth seeing. */}
                  {detail.stops
                    .filter((s) => !detail.actual.some((a) => a.recipientId === s.recipientId))
                    .map((s) => (
                      <tr key={`missed-${s.recipientId}`} style={{ background: "#f9fafb" }}>
                        <td style={S.td}>{s.plannedPosition ?? "—"}</td>
                        <td style={S.td}>—</td>
                        <td style={S.td}>
                          {displayName(s.fullName, masked)}
                          {s.postcode && <span style={S.muted}> {s.postcode}</span>}
                        </td>
                        <td style={S.td}>—</td>
                        <td style={{ ...S.td, ...S.muted }}>Not attempted</td>
                        <td style={S.td}>{s.parcels}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
